import {useEffect, useMemo, useRef, useState} from 'react';

const CARD_MAX_WIDTH_PX = 292;
const CARD_HORIZONTAL_MARGIN_TOTAL_PX = 16;
const FULL_CARD_WIDTH = CARD_MAX_WIDTH_PX + CARD_HORIZONTAL_MARGIN_TOTAL_PX;

interface UseContainerModeSliderLayoutArgs {
    isContainerMode: boolean;
    isMobile: boolean;
    vehiclesLength: number;
}

export const useContainerModeSliderLayout = ({
                                                 isContainerMode,
                                                 isMobile,
                                                 vehiclesLength,
                                             }: UseContainerModeSliderLayoutArgs) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState<number>(0);

    useEffect(() => {
        if (!isContainerMode) return;
        const element = containerRef.current;
        if (!element) return;

        const updateWidth = () => setContainerWidth(element.offsetWidth || 0);
        updateWidth();

        if (typeof ResizeObserver !== 'undefined') {
            const resizeObserver = new ResizeObserver(updateWidth);
            resizeObserver.observe(element);
            return () => resizeObserver.disconnect();
        }
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, [isContainerMode]);

    const slidesToShow = useMemo(() => {
        if (!isContainerMode || !containerWidth) return 1;
        let possible = Math.floor(containerWidth / FULL_CARD_WIDTH);
        if (possible % 2 === 0) {
            possible = possible - 1;
        }
        const capped = Math.max(possible, 1);
        return Math.min(vehiclesLength, capped);
    }, [isContainerMode, containerWidth, vehiclesLength]);

    const centerPaddingPx = useMemo(() => {
        if (!isContainerMode || !containerWidth) return 0;
        const leftover = Math.max(0, containerWidth - slidesToShow * FULL_CARD_WIDTH);
        return Math.floor(leftover / 2);
    }, [isContainerMode, containerWidth, slidesToShow]);

    const allCardsFit = isContainerMode && slidesToShow >= vehiclesLength;
    const showNavButtons = isContainerMode ? (!isMobile && !allCardsFit) : !isMobile;

    return {
        containerRef,
        slidesToShow,
        centerPaddingPx,
        showNavButtons,
    } as const;
};
